/**
 * Shared cell that renders a clickable user identity — real name on top,
 * `@username` underneath when both exist. Used by every drill-down modal so
 * the "user" column looks identical across Time-saved, Users-trend, and the
 * remaining v1.4 drill-downs. Keep the markup here; callers vary only in
 * `data-testid` and the bucket window they forward to `/user/:username`.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';

interface UserNameCellProps {
  username: string;
  realName: string | null;
  /** When set, the `/user/:username` link carries `?from=&to=` so the page opens scoped to the clicked bucket. */
  from?: string;
  to?: string;
  /** Per-caller test id for the underlying <Link> — callers own their own namespace. */
  dataTestId?: string;
}

function buildUserHref(username: string, from?: string, to?: string): string {
  const base = `/user/${encodeURIComponent(username)}`;
  if (!from || !to) return base;
  const params = new URLSearchParams({ from, to });
  return `${base}?${params.toString()}`;
}

export function UserNameCell({
  username,
  realName,
  from,
  to,
  dataTestId,
}: UserNameCellProps): React.ReactElement {
  const primary = realName ?? `@${username}`;
  return (
    <div className="flex flex-col leading-tight min-w-0">
      <Link
        to={buildUserHref(username, from, to)}
        className="truncate text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm"
        title={primary}
        data-testid={dataTestId}
      >
        {primary}
      </Link>
      {realName && (
        <span
          className="truncate text-[10px] text-muted-foreground"
          title={`@${username}`}
        >
          @{username}
        </span>
      )}
    </div>
  );
}
